import React, { useState } from 'react';
import { S } from '../styles';
import { API, authHeaders } from '../utils/helpers';

// The customer's details on a booking: fixing them, whether the confirmation
// text went out, and the warning on a booking form with no phone. Shared by
// Drop-offs, Fittings and Rentals so all three behave the same.
// Backend: PUT /book/staff/:id/contact (routes/bookings.js).

const inputStyle = (T) => ({ width: '100%', background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '8px 10px', color: T.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' });

const whenText = (at) => {
  if (!at) return '';
  const d = new Date(at);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// One line saying whether the confirmation text went out. Bookings made before
// 5 Oct have no record, and say nothing rather than guess.
export function ConfirmTextLine({ T, confirmText, phone }) {
  const c = confirmText;
  if (!c) {
    if (!phone) return <div style={{ fontSize: 12, color: '#c0661a' }}>No phone on this booking, so no texts go to the customer.</div>;
    return null;
  }
  if (c.status === 'sent') return <div style={{ fontSize: 12, color: T.textSub }}>Confirmation text sent{c.at ? ` ${whenText(c.at)}` : ''}.</div>;
  if (c.status === 'none') return phone ? null : <div style={{ fontSize: 12, color: '#c0661a' }}>No phone on this booking, so no texts go to the customer.</div>;
  if (c.status === 'off') return <div style={{ fontSize: 12, color: '#c0661a' }}>No confirmation text: texting is not set up for the shop.</div>;
  return <div style={{ fontSize: 12, color: '#e74c3c' }}>The confirmation text did not go out{c.note ? `: ${c.note}` : '.'}</div>;
}

// Under a staff booking form: the confirmation goes by text only with a phone.
export function NoPhoneHint({ phone }) {
  if (String(phone || '').trim()) return null;
  return <div style={{ fontSize: 12, color: '#c0661a', marginTop: 6 }}>No phone: the customer gets no confirmation text, and no texts about this booking.</div>;
}

// The dialog. `booking` is { id, customerName, customerPhone, customerEmail, workorderId }.
// It stays open after saving to say what happened, because a save can succeed
// while the ticket move or the text does not.
export function ContactEdit({ T, booking, onClose, onSaved }) {
  const [form, setForm] = useState({ customerName: booking.customerName || '', customerPhone: booking.customerPhone || '', customerEmail: booking.customerEmail || '' });
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const contactChanged = form.customerPhone.replace(/\D/g, '') !== String(booking.customerPhone || '').replace(/\D/g, '')
    || form.customerEmail.trim().toLowerCase() !== String(booking.customerEmail || '').trim().toLowerCase();

  const save = async () => {
    setError('');
    if (!form.customerName.trim()) return setError('The customer name is required.');
    setBusy(true);
    try {
      const r = await fetch(`${API}/book/staff/${booking.id}/contact`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...form, notify }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'The details were not saved.');
      setResult(d);
      if (onSaved) onSaved(d);
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  const lines = [];
  if (result) {
    if (result.unchanged) lines.push({ text: 'Nothing changed.' });
    else lines.push({ text: 'The booking now has the corrected details.' });
    if (result.ticket && result.ticket.moved) lines.push({ text: `The Lightspeed ticket moved to the corrected customer${result.ticket.bikeMoved ? ', with its bike' : ''}.` });
    if (result.ticket && !result.ticket.moved && result.ticket.error) lines.push({ text: result.ticket.error, warn: true });
    if (result.text && result.text.status === 'sent') lines.push({ text: 'The customer was texted their booking details at the new number.' });
    if (result.text && result.text.status === 'failed') lines.push({ text: `The text did not go out${result.text.note ? `: ${result.text.note}` : '.'}`, warn: true });
    if (result.text && result.text.status === 'off') lines.push({ text: 'No text: texting is not set up for the shop.', warn: true });
  }

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,10,20,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Customer details</div>
          <button type="button" onClick={onClose} title="Close" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        {!result ? (
          <>
            <div style={{ display: 'grid', gap: 8 }}>
              <label style={{ fontSize: 12, color: T.textSub }}>Name<input style={inputStyle(T)} value={form.customerName} onChange={set('customerName')} /></label>
              <label style={{ fontSize: 12, color: T.textSub }}>Phone<input style={inputStyle(T)} value={form.customerPhone} onChange={set('customerPhone')} /></label>
              <label style={{ fontSize: 12, color: T.textSub }}>Email<input style={inputStyle(T)} value={form.customerEmail} onChange={set('customerEmail')} /></label>
            </div>
            <NoPhoneHint phone={form.customerPhone} />
            {contactChanged && (
              <div style={{ fontSize: 12, color: T.textSub, marginTop: 10, lineHeight: 1.5 }}>
                A new phone or email finds the right customer in Lightspeed{booking.workorderId ? ' and moves the ticket to them' : ''}, the same way a new booking does. A note on the ticket records the change.
              </div>
            )}
            {contactChanged && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: T.text, marginTop: 10 }}>
                <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} style={{ accentColor: '#e67e22' }} />
                Send the booking details to the new phone or email
              </label>
            )}
            {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginTop: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="button" disabled={busy} onClick={save} style={{ ...S.btn(true), background: '#e67e22', color: '#fff', opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={onClose} style={{ ...S.btn(false), color: T.textSub, borderColor: T.inputBorder }}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gap: 6 }}>
              {lines.map((l, i) => <div key={i} role={l.warn ? 'alert' : undefined} style={{ fontSize: 13, color: l.warn ? '#e74c3c' : T.text, lineHeight: 1.5 }}>{l.text}</div>)}
            </div>
            <div style={{ marginTop: 14 }}>
              <button type="button" onClick={onClose} style={{ ...S.btn(true), background: '#e67e22', color: '#fff' }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
